import { DeviceType } from '@app/core/interfaces/c5/device-type.enum';
import { Phone } from '@app/core/interfaces/c5/phone';
import { PhoneNumber } from '@app/core/interfaces/c5/phone-number';
import { DeviceServiceType } from '@app/core/interfaces/device-service-type';
import { DevicePushToken } from './device-push-token';

export class Device {
  id?: number; // Option[Int] = None,
  type: DeviceType; // Device.Type.Value,
  serviceType: DeviceServiceType; // DeviceServiceType.Value = DeviceServiceType.UNKNOWN,
  serviceIndicator?: string; // Option[String] = None,
  sipUri: string; // String,
  userId: number; // Int,
  password: string; // String,
  plainPassword?: string; // Option[String] = None,
  gatewayId?: number; // Option[Int] = None,
  serviceProviderId: number; // Int,
  enterpriseId: number; // Int,
  siteId: number; // Int,
  generic: boolean; // Boolean = false,
  excludeIpFilter?: boolean; // Option[Boolean] = None,
  name: string; // String,
  autoSync: boolean; // Boolean,
  external: boolean; // Boolean = false,
  routingGroupId?: number; // Option[Int] = None,
  featureGroupId?: number; // Option[Int] = None,
  line: number; // Int = 1,
  communicator: boolean; // Boolean = false,
  phoneId?: string; // Option[String] = None,
  phone?: Phone; // Option[Phone] = None,
  phoneNumbers?: PhoneNumber[]; // Option[Seq[PhoneNumber]] = None
  pushTokens?: DevicePushToken[]; // Option[Seq[PushToken]] = None
  // IPEI of the DECT handset (TGP700). Since: 6.2.0.65
  dectIpei?: string; // Option[String] = None,
  // IPUI of the DECT handset (all others). Since: 7.0.4.0
  dectIpui?: string; // Option[String] = None
  hidden?: boolean; // Option[Boolean] = None
  numberRef?: number; // Option[Int] = None
}

export enum DeviceKind {
  DESKTOP_OPERATOR = 'DESKTOP_OPERATOR',
  DESKTOP_CONTROL = 'DESKTOP_CONTROL',
  WEB_CONTROL = 'WEB_CONTROL',
  GENERIC = 'GENERIC',
  IAD = 'IAD',
  MOBILE = 'MOBILE',
  MOBILE_CONTROL = 'MOBILE_CONTROL',
  MS_TEAMS = 'MS_TEAMS',
  EXTERNAL_PBX = 'EXTERNAL_PBX',
  PHONE = 'PHONE',
}

export function toDeviceKind(device: Device): DeviceKind {
  let kind;

  if (device.generic === true) {
    kind = DeviceKind.GENERIC;
  } else if (device.name.startsWith('IAD')) {
    kind = DeviceKind.IAD;
  } else if (device.phone != undefined) {
    kind = DeviceKind.PHONE;
  } else if (device.serviceType === 'DESKTOP_OPERATOR') {
    kind = DeviceKind.DESKTOP_OPERATOR;
  } else if (device.serviceType === 'DESKTOP_CONTROL') {
    kind = DeviceKind.DESKTOP_CONTROL;
  } else if (device.serviceType === 'WEB_CONTROL') {
    kind = DeviceKind.WEB_CONTROL;
  } else if (device.serviceType === 'MOBILE_CONTROL') {
    kind = DeviceKind.MOBILE_CONTROL;
  } else if (device.serviceType === 'MOBILE_COMMUNICATOR_WITHOUT_GSM') {
    kind = DeviceKind.MOBILE_CONTROL;
  } else if (device.type === DeviceType.PHONE && device.name.startsWith('Mobile:')) {
    kind = DeviceKind.MOBILE;
  } else if (device.type === DeviceType.BUSINESS_TRUNK && device.name === 'Microsoft Teams') {
    kind = DeviceKind.MS_TEAMS;
  } else if (device.type === DeviceType.BUSINESS_TRUNK && device.name === 'External PBX') {
    kind = DeviceKind.EXTERNAL_PBX;
  }
  // in case device was not fetched deep (as in ACD/CC agents) then there is no phone data, hence checking this way if it is a phone device
  else if (device.generic === false && device.serviceType === 'UNKNOWN' && device.type === 'PHONE') {
    kind = DeviceKind.PHONE;
  } else {
    kind = DeviceKind.GENERIC;
  }

  return kind;
}

/**
 * Checks if the device is the valid phone in the same way as it is done on back-end.
 * @param device
 * @returns
 */
export function isValidPhone(device: Device): boolean {
  // The check comes from back-end and looks like this:

  // def isValidPhone: Boolean = {
  //   val isPhone = `type` == Device.Type.PHONE && !name.startsWith(
  //     "Mobile: 436760"
  //   ) && (
  //     serviceType == DeviceServiceType.UNKNOWN ||
  //       serviceType == DeviceServiceType.MOBILE_CONTROL ||
  //       serviceType == DeviceServiceType.MOBILE_COMMUNICATOR_WITHOUT_GSM ||
  //       serviceType == DeviceServiceType.DESKTOP_CONTROL ||
  //       serviceType == DeviceServiceType.DESKTOP_OPERATOR ||
  //       serviceType == DeviceServiceType.WEB_CONTROL
  //   )
  //   val isMSTeams =
  //     `type` == Device.Type.BUSINESS_TRUNK && serviceType == DeviceServiceType.UNKNOWN && name == "Microsoft Teams"

  //   isPhone || isMSTeams
  // }

  // implementing the same logic in javascript

  const serviceType = device.serviceType;
  const isPhone =
    device.type === DeviceType.PHONE &&
    !device.name.startsWith('Mobile: 436760') &&
    (serviceType === DeviceServiceType.UNKNOWN ||
      serviceType === DeviceServiceType.MOBILE_COMMUNICATOR_WITHOUT_GSM ||
      serviceType === DeviceServiceType.DESKTOP_CONTROL ||
      serviceType === DeviceServiceType.DESKTOP_OPERATOR ||
      serviceType === DeviceServiceType.WEB_CONTROL);

  const isMSTeams =
    device.type === DeviceType.BUSINESS_TRUNK &&
    serviceType === DeviceServiceType.UNKNOWN &&
    device.name === 'Microsoft Teams';

  return isPhone || isMSTeams;
}
